import { Reducer } from 'redux'
import { ILessonModel } from 'models/lesson.model'
import { QuestionTypeV1 } from 'enum/question-type'
import * as fromActions from '../actions/lessons.action'
import { ILessonState, SurveyQuestionsForm } from '../states/lessons.state'
import { BlockTypes } from '../../enum/block-type.enum'

export const initialState: ILessonState = {
  lesson: {} as ILessonModel,
  currentLessonContent: [],
  lessonInfo: {},
  loading: false,
  loadFailed: false,
  normativeAccessReason: null,
  nextLessonRejection: null,
}

export const reducer: Reducer<ILessonState> = (
  state: ILessonState = initialState,
  { type, payload }
) => {
  switch (type) {
    case fromActions.LESSONS_GET_LESSON:
      return {
        ...state,
        loading: true,
        loadFailed: false,
      }
    case fromActions.LESSONS_GET_LESSON_SUCCESS:
      // A lesson without blocks never requests its content, so its load ends here
      if (!payload?.blockInstances?.length) {
        return {
          ...state,
          lesson: payload,
          currentLessonContent: [],
          loading: false,
          loadFailed: false,
        }
      }
      return {
        ...state,
        lesson: payload,
      }
    case fromActions.LESSONS_LOAD_FAILURE:
      return {
        ...state,
        currentLessonContent: [],
        loading: false,
        loadFailed: true,
      }
    case fromActions.LESSONS_GET_LESSON_CONTENT: {
      return {
        ...state,
        loading: true,
        loadFailed: false,
      }
    }
    case fromActions.LESSONS_GET_LESSON_CONTENT_SUCCESS:
      return {
        ...state,
        currentLessonContent: payload,
        loading: false,
      }

    case fromActions.LESSONS_SET_ATTACHMENTS_TASK: {
      const { index, data } =
        payload as fromActions.LessonsSetAttachmentsTaskParams
      return {
        ...state,
        currentLessonContent: state.currentLessonContent.map((item, i) => {
          if (i === index && item.type === BlockTypes.BlockTask) {
            return {
              ...item,
              response: { ...item.response, attachments: data },
            }
          }
          return item
        }),
      }
    }
    case fromActions.LESSONS_REMOVE_ATTACHMENT_TASK: {
      const { index, token } =
        payload as fromActions.LessonsRemoveAttachmentTaskParams
      return {
        ...state,
        currentLessonContent: state.currentLessonContent.map((item, i) => {
          if (i === index && item.type === BlockTypes.BlockTask) {
            const attachments = item.response?.attachments.filter(
              (attachment) => attachment.token !== token
            )
            return {
              ...item,
              response: { ...item.response, attachments: attachments || [] },
            }
          }
          return item
        }),
      }
    }

    case fromActions.LESSONS_SURVEY_SET_QUESTION_RESPONSE: {
      const { index, questionIndex, value } =
        payload as fromActions.LessonsSetQuestionResponseParams
      return {
        ...state,
        currentLessonContent: state.currentLessonContent.map((item, i) => {
          if (i === index && item.type === BlockTypes.BlockSurvey) {
            return {
              ...item,
              questionsForms: item.questionsForms.map(
                (question, j): SurveyQuestionsForm => {
                  if (j === questionIndex) {
                    const { likert, openEnded } = QuestionTypeV1
                    if (
                      question.questionableType === likert &&
                      value.type === likert
                    )
                      return {
                        ...question,
                        answer: value.answer,
                      }
                    if (
                      question.questionableType === openEnded &&
                      value.type === openEnded
                    )
                      return {
                        ...question,
                        answer: value.answer,
                      }
                  }
                  return question
                }
              ),
            }
          }
          return item
        }),
      }
    }

    case fromActions.LESSONS_GET_NEXT_LESSON:
      return {
        ...state,
        lessonInfo: payload,
        normativeAccessReason: null,
      }
    // next_lesson answers independently of the lesson being loaded, so it must not end that load
    case fromActions.LESSONS_GET_NEXT_LESSON_SUCCESS:
      return {
        ...state,
        normativeAccessReason: null,
      }
    case fromActions.LESSONS_GET_NEXT_LESSON_FAILURE:
      return {
        ...state,
        normativeAccessReason: payload,
      }
    case fromActions.LESSONS_CLEAR_NORMATIVE_ACCESS_ERROR:
      return {
        ...state,
        normativeAccessReason: null,
      }
    case fromActions.LESSONS_NEXT_LESSON_REJECTED:
      return {
        ...state,
        nextLessonRejection: payload,
      }
    case fromActions.LESSONS_CLEAR_NEXT_LESSON_REJECTION:
      return {
        ...state,
        nextLessonRejection: null,
      }
    default:
      return {
        ...state,
      }
  }
}
